import { Box, Grid, Card, CardContent, Typography, Button, useTheme, Paper, Chip } from "@mui/material";
import { Assessment as PLIcon, Book as JournalIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useAuth } from "../../../context/AuthContext";
import { fetchProfitLoss, listJournals } from "../../../services/accounting";

export default function AccountsDashboardPage() {
  useProtectedRoute();
  const theme = useTheme();
  const { token } = useAuth();

  const [data, setData] = useState({ journalCount: 0, profit: 0, from: "", to: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const fromStr = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10);
        const toStr = new Date().toISOString().slice(0,10);
        const [pl, journals] = await Promise.all([
          fetchProfitLoss(token!, { from: fromStr, to: toStr }),
          listJournals(token!)
        ]);
        setData({
          journalCount: Array.isArray(journals) ? journals.length : 0,
          profit: pl?.totals?.netProfit ?? 0,
          from: fromStr,
          to: toStr,
        });
      } catch (err) {
        console.error(err);
      }
    };
    if (token) load();
  }, [token]);

  const stats = [
    { label: "Journals", value: data.journalCount, icon: <JournalIcon fontSize="large" />, color: "primary" as const },
    { label: "Profit (MTD)", value: `$${data.profit.toFixed(2)}` , icon: <PLIcon fontSize="large" />, color: "success" as const },
  ];

  return (
    <Box className="min-h-screen bg-gray-50 p-6">
      <Box className="mb-6">
        <Typography variant="h4" className="text-blue-700 font-bold">Accounts Dashboard</Typography>
        <Typography variant="body2" className="text-gray-600">Financial insights at a glance</Typography>
      </Box>

      <Grid container spacing={3} className="mb-10">
        {stats.map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2, borderLeft: `5px solid ${theme.palette[stat.color].main}` }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box color={theme.palette[stat.color].main}>{stat.icon}</Box>
                  <Box>
                    <Typography variant="h6">{stat.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                    {stat.label.includes('Profit') && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip size="small" label={`This Month: ${data.from} – ${data.to}`} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box className="mb-12">
        <Typography variant="h6" className="mb-4 font-semibold">Quick Access</Typography>
        <Box className="flex flex-wrap gap-4">
          <Button variant="contained" color="primary" href="/accounts/journals">View Journals</Button>
          <Button variant="contained" color="success" href="/accounts/profit-loss">Profit & Loss</Button>
          <Button variant="contained" color="info" href="/accounts/trial-balance">Trial Balance</Button>
          <Button variant="contained" color="warning" href="/accounts/balance-sheet">Balance Sheet</Button>
          <Button variant="contained" color="secondary" href="/accounts/settings">Settings</Button>
          <Button variant="contained" color="inherit" href="/accounts/tools">Migrations</Button>
          <Button variant="outlined" color="primary" href="/accounts/finance/capital">Record Capital</Button>
          <Button variant="outlined" color="primary" href="/accounts/finance/loan">Record Loan</Button>
        </Box>
      </Box>

      <Paper className="p-4">
        <Typography variant="body2" color="text.secondary">No recent transactions feed available.</Typography>
      </Paper>
    </Box>
  );
}
